import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Clock, Users, Zap, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.pexels.com/photos/35176443/pexels-photo-35176443.jpeg" 
            alt="Modern automotive technology"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 via-gray-900/90 to-gray-900/95"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="hero-badge inline-block mb-6 px-6 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full">
            <span className="text-orange-400 font-semibold text-sm">AI-Powered Solutions for Auto Shops</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Turn Missed Calls Into<br />
            <span className="text-orange-500">Booked Jobs — Automatically</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            AI-powered websites and automations built for auto shops that want more customers without more staff.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/contact">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg font-semibold shadow-lg shadow-orange-500/30 transition-all duration-300 hover:scale-105">
                Book a Free AI Growth Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button size="lg" variant="outline" className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500/10 px-8 py-6 text-lg font-semibold transition-all duration-300">
                See How It Works
              </Button>
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 flex flex-wrap justify-center gap-8 text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-orange-500" />
              <span>No Long-Term Contracts</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-orange-500" />
              <span>Built for Auto Shops</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-orange-500" />
              <span>24/7 Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Sound Familiar?
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              These problems cost you money every single day
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Phone, title: "Missed Calls After Hours", desc: "Customers call at 6pm. You're closed. They call your competitor." },
              { icon: Clock, title: "Slow or Outdated Website", desc: "Your site looks like it's from 2010. Customers don't trust it." },
              { icon: Users, title: "No Online Booking", desc: "Customers want to book online. You make them call during business hours." },
              { icon: Zap, title: "Leads Going to Competitors", desc: "You're too busy to respond fast. Someone else gets the job." },
              { icon: Phone, title: "Too Busy to Answer", desc: "You're under a car. Phone's ringing. You lose the lead." },
              { icon: Clock, title: "No Follow-Up System", desc: "Customer says 'I'll call you back.' They never do." }
            ].map((problem, idx) => (
              <Card key={idx} className="bg-gray-800/50 border-gray-700 hover:border-orange-500/50 transition-all duration-300 hover:translate-y-[-4px]">
                <CardContent className="p-6">
                  <div className="bg-orange-500/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                    <problem.icon className="h-7 w-7 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{problem.title}</h3>
                  <p className="text-gray-400">{problem.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                We Install Simple AI Systems That Work For You
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Stop losing money to missed opportunities. Our AI systems handle the boring stuff so you can focus on fixing cars.
              </p>

              <div className="space-y-6">
                {[
                  { icon: Phone, title: "Answer Calls 24/7", desc: "Never miss a lead again. AI answers after hours, weekends, holidays." },
                  { icon: Clock, title: "Book Jobs Automatically", desc: "Customers schedule online. AI handles it. You just show up and work." },
                  { icon: Users, title: "Capture Leads From Your Website", desc: "Fast, mobile-friendly site that turns visitors into paying customers." },
                  { icon: Zap, title: "Follow Up With Customers", desc: "Automatic reminders, review requests, and appointment confirmations." }
                ].map((solution, idx) => (
                  <div key={idx} className="flex gap-4 items-start">
                    <div className="bg-orange-500/10 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <solution.icon className="h-6 w-6 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{solution.title}</h3>
                      <p className="text-gray-400">{solution.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/services" className="inline-block mt-8">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105">
                  View All Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/7709255/pexels-photo-7709255.jpeg"
                alt="AI-powered customer service"
                className="rounded-2xl shadow-2xl shadow-orange-500/20"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Built For Automotive Businesses
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              We specialize in helping these types of shops grow
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { name: "Auto Repair Shops", img: "https://images.pexels.com/photos/2244746/pexels-photo-2244746.jpeg" },
              { name: "Detailers", img: "https://images.pexels.com/photos/1409999/pexels-photo-1409999.jpeg" },
              { name: "Tire & Brake Shops", img: "https://images.pexels.com/photos/162553/keys-workshop-mechanic-tools-162553.jpeg" },
              { name: "Mobile Mechanics", img: "https://images.pexels.com/photos/65623/vehicle-chrome-technology-automobile-65623.jpeg" },
              { name: "Car Washes", img: "https://images.pexels.com/photos/70912/pexels-photo-70912.jpeg" }
            ].map((business, idx) => (
              <Card key={idx} className="bg-gray-800/50 border-gray-700 hover:border-orange-500 transition-all duration-300 hover:translate-y-[-4px] overflow-hidden group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={business.img}
                    alt={business.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
                </div>
                <CardContent className="p-4">
                  <h3 className="text-lg font-bold text-white text-center">{business.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Trusted By Automotive Businesses Across the US
            </h2>
            <p className="text-xl text-gray-400">
              Real shops. Real results.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { quote: "We used to miss at least 5-10 calls a week after hours. Now the AI answers every single one. We've booked 3 new jobs just this month from after-hours calls.", author: "Mike R.", business: "Auto Repair Shop, Texas", stat: "3 New Jobs" },
              { quote: "I was skeptical about AI, but this thing works. Customers love being able to book online. Saves me so much time not playing phone tag.", author: "Sarah L.", business: "Mobile Detailing, California", stat: "Time Saved" },
              { quote: "Best investment we've made. The website looks professional and the AI assistant handles FAQs so we can focus on actual work. Highly recommend.", author: "Carlos M.", business: "Tire Shop, Florida", stat: "Professional Image" }
            ].map((testimonial, idx) => (
              <Card key={idx} className="bg-gray-800/50 border-gray-700 hover:border-orange-500/50 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="text-orange-500 text-5xl mb-4">"</div>
                  <p className="text-gray-300 mb-6 italic">{testimonial.quote}</p>
                  <div className="border-t border-gray-700 pt-4">
                    <p className="text-white font-bold">{testimonial.author}</p>
                    <p className="text-gray-400 text-sm">{testimonial.business}</p>
                    <div className="mt-3 inline-block px-4 py-1 bg-orange-500/10 border border-orange-500/30 rounded-full">
                      <span className="text-orange-400 font-semibold text-sm">{testimonial.stat}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-gray-500 text-sm mt-8">
            *Example testimonials for demonstration purposes
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Stop Losing Customers?
          </h2>
          <p className="text-xl text-orange-100 mb-10">
            Get a free demo customized for your shop. No pressure. No tech talk. Just practical ideas.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-10 py-6 text-xl font-bold shadow-2xl transition-all duration-300 hover:scale-105">
              Get a Free Demo for Your Shop
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
